import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AdminLayout } from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import {
  Menu,
  QrCode,
  Eye,
  LifeBuoy,
  Settings,
  Layers,
  Utensils,
  Flame,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const WEBMENU_BASE_URL = "https://webmenu.megriva.com";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    categories: 0,
    items: 0,
    activeItems: 0,
  });

  useEffect(() => {
    const parsedUser = user || getUserFromStorage();
    const subDomain = getBusinessSubDomain(parsedUser);

    if (!subDomain) return;

    fetchData(parsedUser);
  }, [user]);

  const getUserFromStorage = () => {
    try {
      return JSON.parse(localStorage.getItem("user"));
    } catch {
      return null;
    }
  };

  const getBusinessSubDomain = (u) =>
    (
      u?.businessSubDomain ||
      u?.subDomain ||
      u?.BusinessSubDomain ||
      ""
    )
      .toString()
      .trim();

  const getBusinessId = (u) =>
    u?.businessId ?? u?.BusinessId ?? null;

  const fetchData = async (activeUser) => {
    try {
      setLoading(true);

      const parsedUser = activeUser || user;
      if (!parsedUser) return;

      const subDomain = getBusinessSubDomain(parsedUser);
      const businessId = getBusinessId(parsedUser);

      const catsRes = await api.getCategories({
        businessSubDomain: subDomain,
        page: 0,
        pageSize: 1000,
        visible: true,
      });

      const categories = catsRes?.data ?? [];

      const productResponses = await Promise.all(
        categories.map((c) =>
          api.getProducts({
            categoryId: c.id,
            page: 0,
            pageSize: 1000,
          })
        )
      );

      const allProducts = productResponses.flatMap((r) => r?.data ?? []);

      const uniqueProducts = Array.from(
        new Map(allProducts.map((p) => [p.id, p])).values()
      );

      setStats({
        categories: categories.length,
        items: uniqueProducts.length,
        activeItems: uniqueProducts.filter((p) => p.is_active).length,
      });

      if (businessId) {
        const businessRes = await api.getBusinessById(businessId);
        setBusiness(businessRes?.data ?? null);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, color }) => (
    <div className={`rounded-xl p-5 text-white shadow-md ${color}`}>
      <div className="flex justify-between items-center">
        <div>
          <div className="text-sm opacity-80">{title}</div>
          <div className="text-2xl font-bold mt-1">
            {loading ? "..." : value}
          </div>
        </div>
        <Icon className="w-6 h-6 opacity-80" />
      </div>
    </div>
  );

  const businessSubDomain =
    business?.subDomain || getBusinessSubDomain(user);

  const menuUrl = businessSubDomain
    ? `${WEBMENU_BASE_URL}/${businessSubDomain}`
    : "";

  const rawBusinessLogo =
    business?.logoUrl ||
    business?.logo_url ||
    business?.imageUrl ||
    business?.ImageUrl ||
    "";

  const businessLogo =
    typeof rawBusinessLogo === "string" &&
    /^https?:\/\//i.test(rawBusinessLogo)
      ? rawBusinessLogo
      : "";

  const mobileQuickActions = [
    {
      title: "İçerik Yönetimi",
      description: "Kategori ve içerikleri düzenle",
      path: "/admin/menu",
    },
    {
      title: "QR Kodunu Al",
      description: "Web Menü QR kodunu görüntüle",
      path: "/admin/qr-code",
    },
    {
      title: "Vitrini Ön İzle",
      description: "Müşteri görünümünü kontrol et",
      path: "/admin/preview",
    },
    {
      title: "Kampanyalar",
      description: "Banner ve kampanyaları yönet",
      path: "/admin/banners",
    },
  ];

  const mobileOtherActions = [
    {
      title: "Abonelik",
      description: "Plan ve ödeme bilgilerini yönet",
      path: "/admin/subscription",
    },
    {
      title: "Ayarlar",
      description: "İşletme bilgilerini güncelle",
      path: "/admin/profile",
    },
    {
      title: "Destek",
      description: "Yardım ve destek talebi oluştur",
      path: "/admin/support",
    },
  ];

  return (
    <AdminLayout>
      {/* =====================================================
          MOBILE DASHBOARD
          WebMenuMobile DashboardScreen parity
      ====================================================== */}
      <div
        className="min-h-screen bg-[#F8F8F8] px-5 md:hidden"
        style={{
          paddingTop: "calc(env(safe-area-inset-top, 0px) + 16px)",
          paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 40px)",
        }}
      >
        {loading ? (
          <div className="flex min-h-[300px] flex-col items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-[3px] border-zinc-200 border-t-[#EA580C]" />

            <div className="mt-3 text-sm text-[#71717A]">
              Veriler yükleniyor...
            </div>
          </div>
        ) : (
          <>
            {/* HERO */}
            <section className="mb-5 rounded-3xl border border-[#E4E4E7] bg-white p-5 shadow-[0_8px_24px_rgba(24,24,27,0.06)]">
              <div className="flex items-center gap-3">
                {businessLogo ? (
                  <img
                    src={businessLogo}
                    alt={business?.name || "İşletme logosu"}
                    className="h-14 w-14 shrink-0 rounded-2xl border border-[#E4E4E7] bg-[#F4F4F5] object-cover"
                  />
                ) : null}

                <div className="min-w-0 flex-1">
                  <div className="text-[10px] font-extrabold tracking-[0.8px] text-[#EA580C]">
                    İŞLETME PANELİ
                  </div>

                  <h1 className="mt-0.5 truncate text-[25px] font-extrabold leading-[30px] text-[#18181B]">
                    {business?.name || "İşletmeniz"}
                  </h1>
                </div>

                {menuUrl ? (
                  <div className="flex min-h-[30px] shrink-0 items-center gap-1.5 rounded-full bg-[#DCFCE7] px-2.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#16A34A]" />
                    <span className="text-[10px] font-bold text-[#15803D]">
                      Hazır
                    </span>
                  </div>
                ) : null}
              </div>

              <p className="mt-4 max-w-[340px] text-[13px] leading-5 text-[#71717A]">
                İçerik, QR ve kampanyalarınızı tek yerden yönetin.
              </p>
            </section>

            {/* STATS */}
            <section className="mb-7 grid grid-cols-3 gap-2">
              <div className="flex min-h-[86px] flex-col justify-center rounded-2xl border border-[#E4E4E7] bg-white px-3 py-3">
                <div className="text-[25px] font-extrabold leading-[30px] tabular-nums text-[#18181B]">
                  {stats.categories}
                </div>
                <div className="mt-1 text-[12px] font-semibold text-[#71717A]">
                  Kategori
                </div>
              </div>

              <div className="flex min-h-[86px] flex-col justify-center rounded-2xl border border-[#E4E4E7] bg-white px-3 py-3">
                <div className="text-[25px] font-extrabold leading-[30px] tabular-nums text-[#18181B]">
                  {stats.items}
                </div>
                <div className="mt-1 text-[12px] font-semibold text-[#71717A]">
                  İçerik
                </div>
              </div>

              <div className="flex min-h-[86px] flex-col justify-center rounded-2xl border border-[#EA580C] bg-[#FFF7ED] px-3 py-3">
                <div className="text-[25px] font-extrabold leading-[30px] tabular-nums text-[#EA580C]">
                  {stats.activeItems}
                </div>
                <div className="mt-1 text-[12px] font-semibold text-[#EA580C]">
                  Aktif
                </div>
              </div>
            </section>

            {/* QUICK ACTIONS */}
            <section>
              <div className="mb-3">
                <h2 className="text-xl font-extrabold leading-[26px] text-[#18181B]">
                  Hızlı İşlemler
                </h2>

                <p className="mt-1 text-[13px] leading-5 text-[#71717A]">
                  En sık kullanılan araçlar
                </p>
              </div>

              <div className="mb-7 grid grid-cols-2 gap-3">
                {mobileQuickActions.map((item) => (
                  <button
                    key={item.path}
                    type="button"
                    onClick={() => navigate(item.path)}
                    className="relative min-h-[118px] overflow-hidden rounded-2xl border border-[#E4E4E7] bg-white p-4 text-left shadow-[0_6px_18px_rgba(24,24,27,0.05)] transition active:opacity-60"
                  >
                    <span className="absolute bottom-0 left-0 top-0 w-1 bg-[#EA580C]" />

                    <div className="flex items-start gap-2">
                      <span className="flex-1 text-[15px] font-extrabold leading-5 text-[#18181B]">
                        {item.title}
                      </span>

                      <span className="-mt-1 text-2xl font-light leading-[26px] text-[#A1A1AA]">
                        ›
                      </span>
                    </div>

                    <p className="mt-2 line-clamp-2 text-[12px] leading-4 text-[#71717A]">
                      {item.description}
                    </p>
                  </button>
                ))}
              </div>
            </section>

            {/* LIVE MENU */}
            {menuUrl ? (
              <section>
                <div className="mb-2 ml-1 text-[10px] font-extrabold tracking-[0.7px] text-[#A1A1AA]">
                  CANLI VİTRİN
                </div>

                <button
                  type="button"
                  onClick={() =>
                    window.open(
                      menuUrl,
                      "_blank",
                      "noopener,noreferrer"
                    )
                  }
                  className="mb-7 flex min-h-[78px] w-full items-center gap-3 rounded-2xl border border-[#E4E4E7] bg-white px-4 py-3 text-left transition active:opacity-60"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-[7px]">
                      <span className="h-[7px] w-[7px] shrink-0 rounded-full bg-[#22C55E]" />

                      <span className="text-[13px] font-bold text-[#18181B]">
                        Web Menü bağlantısı
                      </span>
                    </div>

                    <div className="mt-1 truncate text-[12px] text-[#71717A]">
                      {menuUrl}
                    </div>
                  </div>

                  <div className="flex min-h-[38px] min-w-[50px] items-center justify-center rounded-xl bg-[#FFF7ED] px-3">
                    <span className="text-[12px] font-extrabold text-[#EA580C]">
                      Aç
                    </span>
                  </div>
                </button>
              </section>
            ) : null}

            {/* OTHER */}
            <section>
              <div className="mb-2 ml-1 text-[10px] font-extrabold tracking-[0.7px] text-[#A1A1AA]">
                DİĞER
              </div>

              <div className="overflow-hidden rounded-2xl border border-[#E4E4E7] bg-white">
                {mobileOtherActions.map((item, index) => (
                  <div key={item.path}>
                    <button
                      type="button"
                      onClick={() => navigate(item.path)}
                      className="flex min-h-[70px] w-full items-center gap-3 px-4 py-3 text-left transition active:bg-[#F4F4F5]"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-[14px] font-bold text-[#18181B]">
                          {item.title}
                        </div>

                        <div className="mt-1 truncate text-[12px] text-[#71717A]">
                          {item.description}
                        </div>
                      </div>

                      <span className="-mt-0.5 text-[25px] font-light leading-[27px] text-[#A1A1AA]">
                        ›
                      </span>
                    </button>

                    {index < mobileOtherActions.length - 1 ? (
                      <div className="ml-4 h-px bg-[#E4E4E7]" />
                    ) : null}
                  </div>
                ))}
              </div>
            </section>
          </>
        )}
      </div>

      {/* =====================================================
          DESKTOP DASHBOARD
          Premium Web Menü görünümü
      ====================================================== */}
      <div className="mx-auto hidden w-full max-w-[1180px] space-y-6 md:block">
        <section className="relative overflow-hidden rounded-[30px] border border-zinc-200/80 bg-white p-7 shadow-[0_24px_70px_rgba(24,24,27,0.06)] lg:p-8">
          <div
            aria-hidden="true"
            className="absolute -right-16 -top-20 h-64 w-64 rounded-full bg-orange-100/80 blur-3xl"
          />

          <div className="relative flex items-start justify-between gap-8">
            <div className="min-w-0">
              <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-3.5 py-2 text-[11px] font-extrabold uppercase tracking-[0.12em] text-orange-700">
                <span className="h-1.5 w-1.5 rounded-full bg-[#EA580C]" />
                Web Menü Yönetim Alanı
              </div>

              <h1 className="mt-5 text-[34px] font-extrabold tracking-[-0.04em] text-zinc-950 lg:text-[42px]">
                {business?.name || "İşletmeniz"}
              </h1>

              <p className="mt-3 max-w-2xl text-sm font-medium leading-6 text-zinc-500">
                Dijital vitrininizi, içeriklerinizi, kampanyalarınızı ve paylaşım
                araçlarınızı tek yerden yönetin.
              </p>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Button
                  type="button"
                  onClick={() => navigate("/admin/menu")}
                  className="rounded-full bg-[#EA580C] px-5 font-bold text-white shadow-[0_10px_26px_rgba(234,88,12,0.20)] hover:bg-[#C2410C]"
                >
                  İçeriği Yönet
                </Button>

                {menuUrl ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => window.open(menuUrl, "_blank")}
                    className="rounded-full border-zinc-200 bg-white px-5 font-bold text-zinc-800 hover:bg-zinc-50"
                  >
                    Vitrini Görüntüle
                  </Button>
                ) : null}
              </div>
            </div>

            <div className="hidden shrink-0 lg:block">
              <div className="w-[270px] rounded-[24px] border border-zinc-200 bg-[#FAFAF9] p-4">
                <div className="flex items-center gap-3">
                  {businessLogo ? (
                    <img
                      src={businessLogo}
                      alt={business?.name || "İşletme logosu"}
                      className="h-12 w-12 rounded-[15px] border border-zinc-200 bg-white object-cover"
                    />
                  ) : (
                    <div className="grid h-12 w-12 place-items-center rounded-[15px] bg-zinc-950 text-sm font-black text-white">
                      WM
                    </div>
                  )}

                  <div className="min-w-0">
                    <p className="truncate text-sm font-extrabold text-zinc-900">
                      {business?.name || "İşletmeniz"}
                    </p>
                    <div className="mt-1 flex items-center gap-1.5 text-[11px] font-semibold text-emerald-700">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                      {menuUrl ? "Vitrin erişilebilir" : "Kurulum devam ediyor"}
                    </div>
                  </div>
                </div>

                <div className="mt-4 rounded-[16px] bg-white p-3 ring-1 ring-zinc-100">
                  <p className="text-[10px] font-extrabold uppercase tracking-[0.12em] text-zinc-400">
                    Web Menü bağlantısı
                  </p>
                  <p className="mt-1.5 truncate text-xs font-bold text-zinc-700">
                    {menuUrl || "Henüz oluşturulmadı"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="grid grid-cols-3 gap-4">
          {[
            {
              title: "Kategoriler",
              value: stats.categories,
              helper: "Vitrin yapınız",
              icon: Layers,
            },
            {
              title: "Toplam İçerik",
              value: stats.items,
              helper: "Tüm içerikler",
              icon: Utensils,
            },
            {
              title: "Aktif İçerik",
              value: stats.activeItems,
              helper: "Şu anda yayında",
              icon: Flame,
            },
          ].map((item, index) => {
            const Icon = item.icon;

            return (
              <div
                key={item.title}
                className={`rounded-[24px] border p-5 ${
                  index === 2
                    ? "border-orange-200 bg-orange-50/60"
                    : "border-zinc-200/80 bg-white"
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs font-bold text-zinc-500">{item.title}</p>
                    <p
                      className={`mt-2 text-[32px] font-extrabold tracking-[-0.04em] ${
                        index === 2 ? "text-[#EA580C]" : "text-zinc-950"
                      }`}
                    >
                      {loading ? "..." : item.value}
                    </p>
                    <p className="mt-1 text-[11px] font-medium text-zinc-400">
                      {item.helper}
                    </p>
                  </div>

                  <div
                    className={`grid h-11 w-11 place-items-center rounded-[14px] ${
                      index === 2
                        ? "bg-[#EA580C] text-white"
                        : "bg-zinc-100 text-zinc-600"
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                  </div>
                </div>
              </div>
            );
          })}
        </section>

        <section className="grid gap-6 xl:grid-cols-[1.25fr_0.75fr]">
          <div className="rounded-[28px] border border-zinc-200/80 bg-white p-6 shadow-[0_18px_55px_rgba(24,24,27,0.04)]">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#EA580C]">
                  Hızlı İşlemler
                </p>
                <h2 className="mt-2 text-xl font-extrabold tracking-[-0.025em] text-zinc-950">
                  En sık kullandığınız alanlar
                </h2>
                <p className="mt-1 text-sm font-medium text-zinc-500">
                  İçerik, QR, kampanya ve işletme ayarlarına hızlıca ulaşın.
                </p>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3">
              {[
                {
                  title: "İçerik Yönetimi",
                  desc: "Kategori ve içerikleri düzenleyin",
                  icon: Menu,
                  path: "/admin/menu",
                },
                {
                  title: "QR Kod & Bağlantı",
                  desc: "Paylaşım araçlarını görüntüleyin",
                  icon: QrCode,
                  path: "/admin/qr-code",
                },
                {
                  title: "Vitrin Ön İzleme",
                  desc: "Müşteri görünümünü kontrol edin",
                  icon: Eye,
                  path: "/admin/preview",
                },
                {
                  title: "Kampanyalar",
                  desc: "Banner ve kampanyaları yönetin",
                  icon: Settings,
                  path: "/admin/banners",
                },
                {
                  title: "İşletme Ayarları",
                  desc: "İşletme bilgilerinizi güncelleyin",
                  icon: Settings,
                  path: "/admin/profile",
                },
                {
                  title: "Destek",
                  desc: "Yardım ve destek talebi oluşturun",
                  icon: LifeBuoy,
                  path: "/admin/support",
                },
              ].map((item) => {
                const Icon = item.icon;

                return (
                  <button
                    key={item.path}
                    type="button"
                    onClick={() => navigate(item.path)}
                    className="group flex min-h-[102px] items-start gap-4 rounded-[20px] border border-zinc-200/80 bg-[#FAFAF9] p-4 text-left transition hover:-translate-y-0.5 hover:border-orange-200 hover:bg-white hover:shadow-[0_14px_30px_rgba(24,24,27,0.07)]"
                  >
                    <div className="grid h-11 w-11 shrink-0 place-items-center rounded-[14px] bg-white text-zinc-600 shadow-sm ring-1 ring-zinc-100 transition group-hover:bg-[#EA580C] group-hover:text-white">
                      <Icon className="h-5 w-5" />
                    </div>

                    <div className="min-w-0 pt-0.5">
                      <p className="text-sm font-extrabold text-zinc-900">
                        {item.title}
                      </p>
                      <p className="mt-1 text-xs font-medium leading-5 text-zinc-500">
                        {item.desc}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-6">
            <div className="rounded-[28px] bg-zinc-950 p-6 text-white shadow-[0_22px_60px_rgba(24,24,27,0.15)]">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-orange-300">
                    Canlı Vitrin
                  </p>
                  <h2 className="mt-2 text-xl font-extrabold tracking-[-0.025em]">
                    Bağlantınız hazır.
                  </h2>
                </div>

                <div className="grid h-11 w-11 place-items-center rounded-[14px] bg-white/10 text-orange-300">
                  <QrCode className="h-5 w-5" />
                </div>
              </div>

              <p className="mt-3 text-sm font-medium leading-6 text-zinc-400">
                Web Menü bağlantınızı müşterilerinizle QR kod veya doğrudan link
                üzerinden paylaşabilirsiniz.
              </p>

              <div className="mt-5 rounded-[16px] bg-white/10 p-3">
                <p className="truncate text-xs font-semibold text-zinc-200">
                  {menuUrl || "Web Menü bağlantısı henüz hazır değil"}
                </p>
              </div>

              <Button
                type="button"
                onClick={() =>
                  menuUrl
                    ? window.open(menuUrl, "_blank", "noopener,noreferrer")
                    : navigate("/admin/qr-code")
                }
                className="mt-4 w-full rounded-full bg-white font-bold text-zinc-950 hover:bg-zinc-100"
              >
                {menuUrl ? "Vitrini Aç" : "QR Alanına Git"}
              </Button>
            </div>

            <div className="rounded-[28px] border border-zinc-200/80 bg-white p-6">
              <p className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-zinc-400">
                İşletme Profili
              </p>

              <div className="mt-4 space-y-4">
                <div>
                  <p className="text-[11px] font-bold text-zinc-400">İşletme Adı</p>
                  <p className="mt-1 text-sm font-extrabold text-zinc-900">
                    {business?.name || "-"}
                  </p>
                </div>

                <div className="h-px bg-zinc-100" />

                <div>
                  <p className="text-[11px] font-bold text-zinc-400">Telefon</p>
                  <p className="mt-1 text-sm font-semibold text-zinc-700">
                    {business?.phone || "-"}
                  </p>
                </div>

                <div className="h-px bg-zinc-100" />

                <div>
                  <p className="text-[11px] font-bold text-zinc-400">Adres</p>
                  <p className="mt-1 text-sm font-semibold leading-6 text-zinc-700">
                    {business?.address || "-"}
                  </p>
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/admin/profile")}
                className="mt-5 w-full rounded-full border-zinc-200 bg-white font-bold text-zinc-800 hover:bg-zinc-50"
              >
                Bilgileri Düzenle
              </Button>
            </div>
          </div>
        </section>
      </div>
    </AdminLayout>
  );
}